import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, ShieldCheck, Eye, EyeOff, Mail, Lock, Users, Home, Handshake, TrendingUp, Search, Network, Sparkles, Target, ListChecks, ClipboardList, Store, IdCard, BadgeCheck, MapPin, ArrowRight, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BuyerEntryCTA } from "@/components/BuyerEntryCTA";
import { PC_MARK } from "@/lib/assets";
import { BrandLogo } from "@/components/BrandLogo";

const NAVY = "#0F2C59";
const GOLD = "#D4A017";

// Direct Buyer stays feature-flagged (Release 2). When OFF, the buyer card is hidden cleanly
// and the left panel rebalances — the reusable component + /looking-for-property route remain.
const DIRECT_BUYER_ENABLED = process.env.REACT_APP_DIRECT_BUYER_ENABLED === "true";

// 10 strategic pain-point → solution hero slides (fixed copy; accurate, no guarantees).
// Reuses the 4 existing approved /brand/hero-*.webp scenes, themed per slide (no new assets).
const SLIDES = [
  { icon: Network, headline: "Your next opportunity may be outside your network.",
    copy: "Discover real estate professionals beyond your usual circle.",
    solution: "Connections + Professional Discovery", img: "/brand/hero-connect.webp" },
  { icon: ClipboardList, headline: "Have a buyer but no matching property?",
    copy: "Post your Buyer Need and discover matching properties across the network.",
    solution: "Buyer Needs → Property Matching", img: "/brand/hero-s2.webp" },
  { icon: Home, headline: "Have a property but no buyer yet?",
    copy: "Discover Buyer Needs that may match your property.",
    solution: "Properties → Buyer Need Matching", img: "/brand/hero-match.webp" },
  { icon: Sparkles, headline: "Spend less time creating better listings.",
    copy: "Let AI help improve your title, description and selling points.",
    solution: "AI Listing Assistant", img: "/brand/hero-s4.webp" },
  { icon: Target, headline: "Know which opportunities need your attention.",
    copy: "See your Top 3 prioritized opportunities with PropertyConnect Sales Coach.",
    solution: "PropertyConnect Sales Coach", img: "/brand/hero-s5.webp" },
  { icon: ListChecks, headline: "Never lose track of an opportunity.",
    copy: "Manage leads, follow-ups and progress from inquiry to closed deal.",
    solution: "Leads CRM + Follow-ups", img: "/brand/hero-s6.webp" },
  { icon: Handshake, headline: "Turn connections into transactions.",
    copy: "Collaborate with the right real estate professionals around real opportunities.",
    solution: "Collaborations + Connections", img: "/brand/hero-collaborate.webp" },
  { icon: Store, headline: "Give your property more ways to be discovered.",
    copy: "Reach buyers, renters and professionals through PropertyConnect's marketplace.",
    solution: "Internal + Public Marketplace", img: "/brand/hero-s8.webp" },
  { icon: Search, headline: "Find the right property in one marketplace.",
    copy: "Explore properties for Buy, Rent and Stays, including projects and developers.",
    solution: "Buy · Rent · Stays Property Discovery", img: "/brand/hero-s9.webp" },
  { icon: IdCard, headline: "Your professional identity should open doors.",
    copy: "Build and share your Digital Card and Public Professional Profile.",
    solution: "Digital Card + Public Professional Profile", img: "/brand/hero-s10.webp" },
];

// Desktop-only dynamic hero. Mounted lazily so mobile never downloads the scene imagery.
function useDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return isDesktop;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = (e) => setReduced(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

function DynamicHero() {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  const schedule = useCallback(() => {
    if (reduced) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setActive((i) => (i + 1) % SLIDES.length), 7000);
  }, [reduced]);

  useEffect(() => {
    schedule();
    return () => clearTimeout(timerRef.current);
  }, [active, schedule]);

  const goTo = useCallback((i) => {
    setActive(i);
    schedule(); // manual selection restarts the 7s rotation
  }, [schedule]);

  const s = SLIDES[active];
  const Icon = s.icon;

  return (
    <div
      className="hidden lg:block relative overflow-hidden"
      style={{ backgroundColor: NAVY }}
      data-testid="login-hero"
    >
      {/* Rotating scene layers — slow crossfade + subtle Ken Burns (reuses existing hero assets) */}
      {SLIDES.map((p, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity ease-in-out"
          style={{ opacity: i === active ? 1 : 0, transitionDuration: "1600ms" }}
          aria-hidden="true"
          data-testid={`hero-scene-${i}`}
        >
          <div
            className={i === active && !reduced ? "absolute inset-0 pc-kenburns-active" : "absolute inset-0"}
            style={{
              backgroundImage: `url('${p.img}')`,
              backgroundSize: "cover",
              backgroundPosition: "center 40%",
              filter: "brightness(1.13) contrast(1.05) saturate(1.06)",
              transform: i === active ? undefined : "scale(1.02)",
            }}
          />
        </div>
      ))}

      {/* Navy tonal treatments protect headline (top) and controls (bottom) readability across all scenes */}
      <div className="absolute inset-x-0 top-0 h-[38%] bg-gradient-to-b from-[#0F2C59]/55 via-[#0F2C59]/18 to-transparent" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-[52%] bg-gradient-to-t from-[#0F2C59] via-[#0F2C59]/82 to-transparent" aria-hidden="true" />

      <div className="relative h-full w-full flex flex-col justify-between p-10 xl:p-14">
        {/* Stationary brand lockup — consistent across all 10 slides */}
        <div className="ml-auto w-full max-w-xl text-left">
          <p className="text-[12px] font-bold tracking-[0.20em] uppercase" style={{ color: GOLD }}>PropertyConnect</p>
          <div className="h-1 w-16 rounded-full mt-3" style={{ backgroundColor: GOLD }} aria-hidden="true" />
        </div>

        {/* Per-slide story: solution label → headline → supporting copy → counter + dots */}
        <div className="ml-auto w-full max-w-xl text-left">
          <div key={active} className="pc-hero-copy" data-testid={`hero-slide-${active}`}>
            <div
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-4"
              style={{ backgroundColor: "rgba(212,160,23,0.16)", border: "1px solid rgba(212,160,23,0.5)" }}
              data-testid="hero-solution"
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={2} style={{ color: GOLD }} />
              <span className="text-[12.5px] font-bold tracking-wide" style={{ color: GOLD }}>{s.solution}</span>
            </div>
            <h2
              className="text-4xl xl:text-[46px] font-extrabold leading-[1.08] tracking-tight text-white"
              style={{ textShadow: "0 1px 2px rgba(10,26,54,0.9), 0 2px 16px rgba(10,26,54,0.6)" }}
              data-testid="hero-headline"
            >
              {s.headline}
            </h2>
            <p
              className="mt-4 text-[16px] xl:text-[17px] leading-relaxed font-medium text-white/95 max-w-md"
              style={{ textShadow: "0 0 20px rgba(10,26,54,0.9), 0 1px 2px rgba(10,26,54,0.95)" }}
            >
              {s.copy}
            </p>
          </div>

          {/* Slide counter + navigation dots */}
          <div className="mt-8 flex items-center gap-4">
            <span className="text-[13px] font-bold tabular-nums" style={{ color: GOLD }} data-testid="hero-counter">
              {String(active + 1).padStart(2, "0")} <span className="text-white/50">/ {SLIDES.length}</span>
            </span>
            <div className="flex items-center gap-1.5" role="tablist" aria-label="Hero slides" data-testid="hero-dots">
              {SLIDES.map((p, i) => {
                const on = i === active;
                return (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    aria-label={`Go to slide ${i + 1}: ${p.solution}`}
                    onClick={() => goTo(i)}
                    data-testid={`hero-dot-${i}`}
                    className="h-1.5 rounded-full transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A017]/60"
                    style={{
                      width: on ? "1.5rem" : "0.375rem",
                      backgroundColor: on ? GOLD : "rgba(255,255,255,0.35)",
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Subtle canonical brand line */}
          <p className="mt-6 text-[11px] font-semibold tracking-[0.16em] uppercase" style={{ color: "rgba(255,255,255,0.55)" }}>
            Connect. Match. Collaborate. <span style={{ color: GOLD }}>Close.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isDesktop = useDesktop();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return; // guard against duplicate submits
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      nav(loc.state?.from || "/", { replace: true });
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-[minmax(380px,31%)_1fr] bg-white"
      style={{ fontFamily: "'Poppins', system-ui, sans-serif", color: NAVY }}
    >
      {/* ---------------- LEFT: authentication + buyer entry ---------------- */}
      <div
        className="relative flex items-start lg:items-center justify-center px-6 pt-9 pb-8 sm:px-10 overflow-y-auto overflow-x-hidden"
        style={{ background: "linear-gradient(165deg, #FBFCFE 0%, #F1F5FB 52%, #E9EFF8 100%)" }}
      >
        {/* Mobile-only aspirational architectural treatment — fades softly into the light page, never behind the form */}
        <div className="lg:hidden absolute inset-x-0 top-0 h-[300px] pointer-events-none z-0" aria-hidden="true">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('/brand/mobile-login-arch.webp')",
              opacity: 0.5,
              WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 48%, rgba(0,0,0,0) 100%)",
              maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 48%, rgba(0,0,0,0) 100%)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2C59]/[0.12] via-[#EEF3FA]/65 to-[#E9EFF8]" />
        </div>

        <div className="relative z-10 w-full max-w-md pc-reveal">
          {/* Official PropertyConnect brand lockup — clean emblem + wordmark (no baked tagline) */}
          <Link to="/login" className="inline-flex items-center gap-2.5 mb-8" aria-label="PropertyConnect home">
            <BrandLogo className="h-[92px] sm:h-[96px]" />
          </Link>

          <h1 className="text-3xl sm:text-[34px] font-bold leading-[1.15] tracking-tight" style={{ color: NAVY }}>
            Welcome back to<br />PropertyConnect
          </h1>
          {/* P1: subtle premium gold accent rule beneath the heading */}
          <div className="h-[3px] w-11 rounded-full mt-3.5" style={{ backgroundColor: GOLD }} aria-hidden="true" />
          <p className="text-[15px] mt-3.5 font-semibold leading-snug" style={{ color: NAVY }} data-testid="login-subheadline">
            Continue building your real estate opportunities.
          </p>
          <p className="text-sm mt-1.5 leading-relaxed font-medium" style={{ color: "#26384F" }}>
            Access your properties, Buyer Needs, matches, and active collaborations—all in one place.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="login-form">
            <div>
              <Label htmlFor="login-email" className="text-[13px] font-semibold" style={{ color: NAVY }}>Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="login-email" data-testid="login-email" type="email" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email"
                  className="h-12 pl-10 rounded-xl border-[#0F2C59]/20 bg-white/95 text-[#0F2C59] placeholder:text-slate-400 hover:border-[#0F2C59]/40 focus-visible:border-[#0F2C59] focus-visible:ring-2 focus-visible:ring-[#D4A017]/40 transition-colors" />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="login-password" className="text-[13px] font-semibold" style={{ color: NAVY }}>Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold hover:underline" style={{ color: GOLD }}>Forgot password?</Link>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="login-password" data-testid="login-password" type={showPass ? "text" : "password"} autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                  className="h-12 pl-10 pr-11 rounded-xl border-[#0F2C59]/20 bg-white/95 text-[#0F2C59] placeholder:text-slate-400 hover:border-[#0F2C59]/40 focus-visible:border-[#0F2C59] focus-visible:ring-2 focus-visible:ring-[#D4A017]/40 transition-colors" />
                <button type="button" data-testid="toggle-password" onClick={() => setShowPass((s) => !s)}
                  aria-label={showPass ? "Hide password" : "Show password"} aria-pressed={showPass}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A017]/50">
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {err && <p data-testid="login-error" role="alert" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-12 font-bold rounded-xl text-[15px] shadow-sm hover:brightness-95 transition"
              style={{ backgroundColor: GOLD, color: NAVY }}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          {/* Secondary actions — compact navy-tinted panel integrated with the login card */}
          <div className="mt-5 rounded-2xl border border-[#0F2C59]/12 bg-[#0F2C59]/[0.035] px-4 py-3.5" data-testid="secondary-actions">
            <div className="flex items-start gap-2.5">
              <UserPlus className="h-[18px] w-[18px] shrink-0 mt-[3px]" strokeWidth={2} style={{ color: NAVY }} />
              <div className="min-w-0">
                <p className="text-[13.5px] font-medium leading-snug" style={{ color: "#243657" }}>New to PropertyConnect?</p>
                <Link to="/register" data-testid="go-register" className="inline-flex items-center gap-1 mt-0.5 text-sm font-bold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A017]/50 rounded" style={{ color: GOLD }}>
                  Create account <ArrowRight className="h-[15px] w-[15px]" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
            <div className="my-3 h-px bg-[#0F2C59]/10" aria-hidden="true" />
            <div className="flex items-start gap-2.5">
              <Search className="h-[18px] w-[18px] shrink-0 mt-[3px]" strokeWidth={2} style={{ color: NAVY }} />
              <div className="min-w-0">
                <p className="text-[13.5px] font-medium leading-snug" style={{ color: "#243657" }}>Looking for your next property to buy, rent, or stay in?</p>
                <Link to="/marketplace" data-testid="go-marketplace" className="inline-flex items-center gap-1 mt-0.5 text-sm font-bold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A017]/50 rounded" style={{ color: GOLD }}>
                  Explore the Marketplace <ArrowRight className="h-[15px] w-[15px]" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>

          {/* Direct Buyer CTA — shown only when the Release 2 flag is enabled */}
          {DIRECT_BUYER_ENABLED && (
            <div className="mt-6"><BuyerEntryCTA /></div>
          )}

          {/* Trust signals — quiet, readable row (not clickable) */}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2.5" data-testid="trust-component">
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0" strokeWidth={2} style={{ color: GOLD }} />
              <span className="text-[12.5px] font-semibold leading-tight" style={{ color: "#243657" }}>Secure &amp; privacy-conscious</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 shrink-0" strokeWidth={2} style={{ color: GOLD }} />
              <span className="text-[12.5px] font-semibold leading-tight" style={{ color: "#243657" }}>Verified professionals</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 shrink-0" strokeWidth={2} style={{ color: GOLD }} />
              <span className="text-[12.5px] font-semibold leading-tight" style={{ color: "#243657" }}>Built for Philippine real estate</span>
            </div>
          </div>

          {/* Subtle canonical brand tagline — mobile only, lightweight */}
          <p className="lg:hidden text-center text-[11px] font-semibold tracking-[0.14em] uppercase mt-6" style={{ color: "#5A6E8C" }}>
            Connect. Match. Collaborate. <span style={{ color: GOLD }}>Close.</span>
          </p>

          {/* Login-local legal footer */}
          <footer role="contentinfo" data-testid="login-legal-footer" className="mt-7 text-center">
            <nav aria-label="Legal" className="flex items-center justify-center gap-2.5 text-xs flex-wrap">
              <Link to="/terms" className="font-medium hover:underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F2C59]/40 rounded" style={{ color: "#33465F" }}>Terms of Service</Link>
              <span style={{ color: "#9DB0C8" }}>·</span>
              <Link to="/privacy" className="font-medium hover:underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F2C59]/40 rounded" style={{ color: "#33465F" }}>Privacy Policy</Link>
            </nav>
            <p className="text-xs mt-2" style={{ color: "#3A4C66" }}>© 2026 PropertyConnect. All rights reserved.</p>
            <p className="text-xs mt-0.5" style={{ color: "#3A4C66" }}>Operated by KODA Technologies Inc.</p>
          </footer>
        </div>
      </div>

      {/* ---------------- RIGHT: Dynamic 4-scene hero (desktop only) ---------------- */}
      {isDesktop && <DynamicHero />}
    </div>
  );
}
